/*
  ##Custom Code##
  Eigenes JavaScript für Occulto. Wird in layout/theme.liquid nach theme.js geladen.
  Bei Prestige-Updates bleibt diese Datei unverändert erhalten.
*/
import { CartDrawer, Drawer, QuickBuyModal } from 'theme';

// Quick-Buy („Optionen auswählen“) öffnet als Seitenleiste von rechts, genau wie der Warenkorb.
// Beide Elemente nutzen dieselbe Shadow-DOM-Struktur, daher reicht es, Vorlage und Animation des
// Drawers auf das Quick-Buy-Modal zu übertragen.
const drawerBehavior = Object.getOwnPropertyDescriptors(Drawer.prototype);

['shadowDomTemplate', 'openFrom', 'createEnterAnimationControls', 'createLeaveAnimationControls'].forEach((key) => {
  if (drawerBehavior[key]) {
    Object.defineProperty(QuickBuyModal.prototype, key, drawerBehavior[key]);
  }
});

// Gratisversand-Balken: Die Breite kommt aus Liquid. Ändert sich der Warenkorb ohne Neu-Rendern,
// setzt Prestige nur das Attribut total-price neu, deshalb ziehen wir die Breite hier nach.
document.addEventListener('cart:change', () => {
  document.querySelectorAll('free-shipping-bar').forEach((bar) => {
    const fill = bar.querySelector('.free-shipping-bar__fill');
    const threshold = parseFloat(bar.getAttribute('threshold'));

    if (fill && threshold > 0) {
      fill.style.width = `${Math.min(100, (bar.totalPrice / threshold) * 100)}%`;
    }
  });
});

// Kollektion mit Farbkarten: "Mehr laden" blendet die nächsten Karten ein (alle Karten sind schon gerendert,
// damit die Reihenfolge über alle Farben stimmt)
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-color-cards-more]');
  if (!button) return;

  const list = document.getElementById(button.getAttribute('aria-controls'));
  const step = parseInt(button.dataset.colorCardsMore, 10) || 24;
  const hiddenCards = [...(list?.querySelectorAll('product-card[hidden]') || [])];

  hiddenCards.slice(0, step).forEach((card) => card.removeAttribute('hidden'));

  if (hiddenCards.length <= step) {
    button.closest('.color-cards-more')?.remove();
  }
});

// Filterleiste oben (Kollektion): immer nur ein Filter-Menü offen, Klick daneben oder Esc schließt es
document.addEventListener('toggle', (event) => {
  const opened = event.target;
  if (!opened.matches?.('.facets-dropdown') || !opened.open) return;

  document.querySelectorAll('.facets-dropdown[open]').forEach((details) => {
    if (details !== opened) details.open = false;
  });
}, true);

document.addEventListener('click', (event) => {
  if (event.target.closest('.facets-dropdown')) return;
  document.querySelectorAll('.facets-dropdown[open]').forEach((details) => (details.open = false));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.facets-dropdown[open]').forEach((details) => (details.open = false));
});

// Filterleiste: Anzahl der Farbkarten anzeigen (Shopify zählt nur Produkte). Nach jedem Filtern wird die
// Section neu eingesetzt, deshalb beobachten wir Änderungen im Dokument.
const updateCardCounts = () => {
  document.querySelectorAll('[data-card-count-for]').forEach((element) => {
    const count = document.getElementById(element.dataset.cardCountFor)?.querySelectorAll('product-card').length || 0;
    const label = count === 1 ? element.dataset.labelOne : element.dataset.labelOther;
    const text = `${count} ${label}`;
    if (element.textContent !== text) element.textContent = text;
  });
};

// Filter links (Kollektion, Desktop): Der Schalter neben „Filter“ blendet die Filterspalte aus und ein (Klasse am
// <html>) und merkt sich den Zustand im Browser. Die Klasse wird schon im Abschnitt vor dem Rendern gesetzt
// (main-collection.liquid).
const syncSidebarToggles = () => {
  const hidden = document.documentElement.classList.contains('occulto-filter-hidden');
  document.querySelectorAll('[data-sidebar-toggle]').forEach((toggle) => (toggle.checked = !hidden));
};

document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-sidebar-toggle]')) return;
  const hidden = !event.target.checked;
  document.documentElement.classList.toggle('occulto-filter-hidden', hidden);
  try { localStorage.setItem('occulto-filter-hidden', hidden ? '1' : '0'); } catch (e) {}
  syncSidebarToggles();
  updateSidebarDocked();
});

// Filter links (Desktop): Das Trusted-Shops-Siegel unten links liegt über der Filterspalte. Sobald die Spalte unten ins
// Bild kommt, wird es ausgeblendet (Klasse am <html>, siehe custom.css); oberhalb davon bleibt es sichtbar.
const updateSidebarDocked = () => {
  const sidebar = document.querySelector('.shopify-section--main-collection .facets-sidebar');
  const rect = sidebar?.offsetParent != null && sidebar.getBoundingClientRect();
  const visible = rect && rect.top < window.innerHeight && rect.bottom > 0;
  document.documentElement.classList.toggle('occulto-sidebar-docked', Boolean(visible));
};
window.addEventListener('scroll', updateSidebarDocked, { passive: true });

// Filter links (Desktop): Prestige scrollt nach jedem Filtern zur Produktliste (theme.js, facet:update). Hier soll
// die Seite stehen bleiben, deshalb wird dieses eine scrollIntoView übersprungen.
const originalScrollIntoView = Element.prototype.scrollIntoView;
Element.prototype.scrollIntoView = function (...args) {
  const keepPosition = this.matches('.collection')
    && this.closest('.shopify-section--main-collection')?.querySelector('.facets-sidebar')
    && window.matchMedia('(min-width: 1000px)').matches;
  if (keepPosition) return;
  return originalScrollIntoView.apply(this, args);
};

let cardCountFrame;
updateCardCounts();
syncSidebarToggles();
updateSidebarDocked();
new MutationObserver(() => {
  cancelAnimationFrame(cardCountFrame);
  cardCountFrame = requestAnimationFrame(() => {
    updateCardCounts();
    syncSidebarToggles();
    updateSidebarDocked();
  });
}).observe(document.body, { childList: true, subtree: true });

// Quick-Buy und Warenkorb am Desktop als angedockte Seitenleiste: keine Abdunkelung, die Seite bleibt scroll- und
// klickbar und rückt um die Breite der Leiste zur Seite (Klasse "side-panel-docked", siehe custom.css).
// Quick-Buy aus dem Warenkorb („Dazu passt“) legt sich über den Warenkorb. Unter 1570px (Laptops, Tablet, Handy)
// bleiben es normale Fenster mit Abdunkelung, sonst wird die Seite zu schmal.
const sidePanelDocked = window.matchMedia('(min-width: 1570px)');
const sidePanelSelector = 'quick-buy-modal, cart-drawer';

[QuickBuyModal, CartDrawer].forEach((PanelClass) => {
  Object.defineProperties(PanelClass.prototype, {
    shouldLock: { configurable: true, get: () => !sidePanelDocked.matches },
    shouldTrapFocus: { configurable: true, get: () => !sidePanelDocked.matches }
  });
});

const updateSidePanelDocked = () => {
  const isOpen = sidePanelDocked.matches && [...document.querySelectorAll(sidePanelSelector)].some((panel) => panel.open);
  document.documentElement.classList.toggle('side-panel-docked', isOpen);
};

document.addEventListener('dialog:before-show', (event) => {
  const panel = event.target;
  if (!panel.matches?.(sidePanelSelector) || !sidePanelDocked.matches) return;

  panel.setAttribute('aria-modal', 'false');

  // Immer nur ein Quick-Buy offen
  if (panel.matches('quick-buy-modal')) {
    document.querySelectorAll('quick-buy-modal[open]').forEach((modal) => modal !== panel && modal.hide());
  }

  updateSidePanelDocked();
}, true);

document.addEventListener('dialog:before-hide', (event) => {
  if (!event.target.matches?.(sidePanelSelector)) return;
  requestAnimationFrame(updateSidePanelDocked);
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !document.documentElement.classList.contains('side-panel-docked')) return;
  document.querySelectorAll(sidePanelSelector).forEach((panel) => panel.open && panel.hide());
});

sidePanelDocked.addEventListener('change', () => {
  document.querySelectorAll(sidePanelSelector).forEach((panel) => panel.open && panel.hide());
});

// Preis wie im Sale: Ist eine Mengenstufe mit Rabatt gewählt (Fly-Bundler auf der Produktseite oder Bundler im Quick-Buy),
// zeigt der Preis oben den Gesamtpreis der Stufe rot mit „Statt:“ und Streichpreis, „nur … / Paar“ rechnet mit.
// Die Original-Elemente bleiben im Preis-Block und werden nur versteckt, ohne Rabattstufe kommen sie zurück.
const parseMoney = (text) => Math.round(parseFloat((text || '').replace(/[^0-9,]/g, '').replace(',', '.')) * 100) || 0;

const pricePerUnitText = (element, total, boxes) => {
  const packSize = parseFloat(element.dataset.packSize);
  if (!packSize) return null;
  // Normal auf volle Cent gerundet; ergibt das ,00 oder ,50, einen Cent weniger (wie in price-list.liquid)
  let cents = Math.round(total / (boxes * packSize) + 1e-9);
  if (cents > 0 && cents % 50 === 0) cents -= 1;
  return `nur ${formatMoney(cents)} / ${element.dataset.packUnit}`;
};

const showTierPrice = (priceList, tier, updateUnitPrice = true) => {
  if (!priceList) return;
  const discounted = tier && tier.original > tier.total;
  const key = discounted ? `${tier.total}-${tier.original}` : '';
  if ((priceList.dataset.occultoTier || '') === key) return;
  priceList.dataset.occultoTier = key;

  priceList.querySelectorAll('[data-occulto-tier-price]').forEach((element) => element.remove());
  const originals = priceList.querySelectorAll(':scope > sale-price, :scope > compare-at-price, :scope > .compare-label');
  originals.forEach((element) => (element.hidden = discounted));

  // „nur … / Paar“: auf der Produktseite rechnet das Skript weiter unten (custom.js), hier nur im Quick-Buy
  const unitPrice = updateUnitPrice && priceList.querySelector('.unit-price-extra[data-pack-size]');
  if (unitPrice) {
    unitPrice.dataset.occultoText ??= unitPrice.textContent;
    unitPrice.textContent = discounted ? pricePerUnitText(unitPrice, tier.total, tier.boxes) || unitPrice.dataset.occultoText : unitPrice.dataset.occultoText;
  }

  if (!discounted) return;

  // Gleiche Klassen wie Prestige im Sale (price-list.liquid): Produktseite h4/h5, Karten und Quick-Buy ohne
  const saleClass = (originals[0]?.className || '').replace('text-subdued', '').trim();
  const compareClass = `${saleClass.includes('h4') ? 'h5 ' : saleClass.includes('h6') ? 'h6 ' : ''}text-subdued line-through`;
  originals[0]?.insertAdjacentHTML('beforebegin', `<sale-price class="${saleClass.replace('text-on-sale', '')} text-on-sale" data-occulto-tier-price>
      <span class="sr-only">Angebotspreis</span>${formatMoney(tier.total)}</sale-price><span class="compare-label" data-occulto-tier-price>Statt: </span><compare-at-price class="${compareClass}" data-occulto-tier-price>
      <span class="sr-only">Normaler Preis</span>${formatMoney(tier.original)}</compare-at-price>`);
};

// Produktseite: Fly zeichnet Stufen und Prestige den Preis bei jeder Auswahl neu, deshalb auf Änderungen reagieren
const updateProductPageTierPrice = () => {
  document.querySelectorAll('.product-info').forEach((info) => {
    if (info.closest('quick-buy-modal, [data-occulto-bundle-scope]')) return;
    const scope = info.closest('.shopify-section') || document;
    const selected = scope.querySelector('[data-offer-id][data-qty-label]:has(input[type="radio"]:checked)');
    if (!selected) return;
    const tier = {
      boxes: parseFloat(selected.dataset.qtyLabel) || 1,
      total: parseMoney(selected.querySelector('.gfbVolumeDiscountDiscountPrice')?.textContent),
      original: parseMoney(selected.querySelector('.gfbVolumeDiscountOriginalPrice')?.textContent)
    };
    showTierPrice(info.querySelector('price-list'), tier, false);
    // Sticky-Leiste unten (main-product.liquid): gleicher Stufenpreis, „nur … / Paar“ rechnet hier mit
    scope.querySelectorAll('product-sticky-bar price-list').forEach((priceList) => showTierPrice(priceList, tier));
  });
};

if (document.querySelector('.product-info')) {
  let tierPriceFrame;
  new MutationObserver(() => {
    cancelAnimationFrame(tierPriceFrame);
    tierPriceFrame = requestAnimationFrame(updateProductPageTierPrice);
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'class'] });

  // Fly setzt die Auswahl per Skript und stoppt den Klick, deshalb in der Capture-Phase mithören
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-offer-id][data-qty-label]')) return;
    setTimeout(updateProductPageTierPrice, 50);
    setTimeout(updateProductPageTierPrice, 400);
  }, true);
}

// Mengenstaffel (snippets/occulto-staffel.liquid, Ersatz für Fly Bundles): Produktseite (große Kästen mit Preis),
// Quick-Buy und Produkt-Highlight der Startseite (schlanke Knöpfe). Die gewählte Stufe ist die Menge; jede Zeile bekommt
// das Merkmal _occulto_staffel, darauf rechnet die Funktion „Occulto-Rabatte“ den Rabatt pro Produkt (alle Farben).
// Die Felder hängen per form-Attribut am Formular, Prestige schickt sie beim Hinzufügen mit.
const formatMoney = (cents) => window.themeVariables.settings.moneyFormat
  .replace(/\{\{\s*\w+\s*\}\}/, (cents / 100).toFixed(2).replace('.', ','));

// Ersparnis in Euro ohne „,00“ bei glatten Beträgen (Adventskalender: „Spare 10 €“)
const formatSaving = (cents) => (cents % 100 === 0 ? `${cents / 100} €` : formatMoney(cents));

// Stufen der Variante: Bezeichnung aus dem Metafeld („3 Paar“ → 3 / 6 / 9 Paar), Mützen/Sets als Stückzahl
const staffelTiers = (box, variantId) => {
  const [price, compare, units] = JSON.parse(box.querySelector('script').textContent)[variantId] || [];
  if (price == null) return [];
  const onSale = compare > price; // Sale: nur die Menge, keine zusätzliche Ersparnis (wie in der Rabattfunktion)
  const match = String(units || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
  const size = match ? parseFloat(match[1].replace(',', '.')) : 1;
  const unit = match?.[2] || 'Stück';
  const art = box.dataset.staffelArt;

  return box.dataset.staffelProzente.split(',').map((value, index) => {
    const quantity = index + 1;
    const percent = onSale ? 0 : Number(value) || 0;
    let title = `${size * quantity} ${unit}`;
    if (art === 'muetze') title = quantity === 1 ? '1 Mütze' : `${quantity} Mützen`;
    if (art === 'set') title = quantity === 1 ? '1 Set' : `${quantity} Sets`;
    if (art === 'stueck') title = `${quantity} Stück`;
    const original = Math.max(price, compare || 0) * quantity;
    const total = Math.round(price * quantity * (1 - percent / 100));
    const saving = 'staffelEuro' in box.dataset ? formatSaving(price * quantity - total) : `${percent}%`;
    return { index, quantity, percent, title, total, original, saving };
  });
};

const staffelPriceLists = (box) => box.closest('quick-buy-modal')?.querySelectorAll('.quick-buy-modal__header-text price-list')
  || box.closest('[data-occulto-bundle-scope]')?.querySelectorAll('.product-info price-list')
  // Produktseite: Preis im Kopf und in der Sticky-Leiste unten (main-product.liquid)
  || [box.closest('.product-info')?.querySelector('price-list'), ...(box.closest('.shopify-section')?.querySelectorAll('product-sticky-bar price-list') || [])].filter(Boolean);

const selectQuickBundleTier = (box, index) => {
  const tier = staffelTiers(box, box.dataset.variantId)[index];
  if (!tier) return;
  box.querySelector('input[name="quantity"]').value = tier.quantity;
  box.querySelector('[data-bundle-selected]').textContent = tier.title;

  // Preis oben wie im Sale (rot mit Streichpreis), „nur … / Paar“ rechnet mit
  staffelPriceLists(box).forEach((priceList) => showTierPrice(priceList, {
    boxes: tier.quantity,
    total: tier.total,
    original: tier.percent > 0 ? tier.original : 0
  }));
};

const renderQuickBundle = (box, variantId) => {
  const form = document.forms[box.dataset.formId];
  box.dataset.variantId = variantId || form?.elements.id?.value || '';
  const tiers = staffelTiers(box, box.dataset.variantId);

  // Quick-Buy: Warenkorb-Button in eine feste Fußzeile der Leiste (custom.css), der Bundler steht darüber und scrollt mit.
  // Das Teil-Neuzeichnen beim Variantenwechsel ersetzt nur den Button in seinem Block, die Hülle bleibt stehen.
  // Produktseite: Der Bundler steht als eigener Block schon über dem Warenkorb-Button.
  const scope = box.closest('.quick-buy-modal__info-wrapper, [data-occulto-bundle-scope]');
  const buyBlock = scope?.querySelector('[data-block-type="buy-buttons"]');
  if (buyBlock && box.closest('quick-buy-modal') && !buyBlock.parentElement.matches('.occulto-quick-buy-footer')) {
    const footer = document.createElement('div');
    footer.className = 'occulto-quick-buy-footer';
    buyBlock.before(footer);
    footer.append(buyBlock);
  }
  if (scope) (buyBlock?.parentElement.matches('.occulto-quick-buy-footer') ? buyBlock.parentElement : buyBlock)?.before(box);

  if (!form || tiers.length < 2) {
    box.hidden = true;
    return;
  }

  const selected = Math.min(Number(box.querySelector('input[type="radio"]:checked')?.value ?? 0), tiers.length - 1);
  // Sale-Variante: keine Ersparnis, nur Menge + Preis → flachere Kästen (custom.css)
  box.classList.toggle('occulto-quick-bundle--ohne-rabatt', tiers.every((tier) => tier.percent === 0));
  box.innerHTML = `${box.querySelector('script').outerHTML}
    <p class="occulto-quick-bundle__title">Menge: <span data-bundle-selected></span></p>
    <div class="occulto-quick-bundle__tiers" style="--occulto-tiers: ${tiers.length}">
      ${tiers.map((tier) => `<label class="occulto-quick-bundle__tier">
        <input type="radio" class="sr-only" name="occulto-quick-bundle-${box.dataset.formId}" value="${tier.index}" ${tier.index === selected ? 'checked' : ''}>
        ${tier.index === 1 ? '<span class="occulto-quick-bundle__badge">Beliebt</span>' : ''}
        <span class="occulto-quick-bundle__name">${tier.title}</span>
        ${tier.percent > 0 ? `<span class="occulto-quick-bundle__save">Spare ${tier.saving}</span>` : ''}
        <span class="occulto-quick-bundle__price">${formatMoney(tier.total)}${tier.percent > 0 ? ` <s>${formatMoney(tier.original)}</s>` : ''}</span>
      </label>`).join('')}
    </div>
    <input type="hidden" form="${box.dataset.formId}" name="quantity">
    <input type="hidden" form="${box.dataset.formId}" name="properties[_occulto_staffel]" value="1">`;

  box.hidden = false;
  selectQuickBundleTier(box, selected);
};

// Auf Produktseiten baut Fly jede Anfrage mit form_type=product nach der Auswahl seines eigenen Bundlers um, auch die aus
// dem Quick-Buy (dann käme Menge und Produkt der Seite statt der Auswahl hier). Die Warenkorb-API braucht form_type nicht.
document.addEventListener('submit', (event) => {
  if (!event.target.closest?.('quick-buy-modal, [data-occulto-bundle-scope]')) return;
  event.target.querySelectorAll('input[name="form_type"], input[name="properties[_modifyPayloadQB]"]').forEach((input) => (input.disabled = true));
}, true);

document.addEventListener('change', (event) => {
  const radio = event.target.closest('[data-occulto-quick-bundle] input[type="radio"]');
  if (radio) selectQuickBundleTier(radio.closest('[data-occulto-quick-bundle]'), Number(radio.value));
});

document.addEventListener('dialog:before-show', (event) => {
  if (!event.target.matches?.('quick-buy-modal')) return;
  event.target.querySelectorAll('[data-occulto-quick-bundle]').forEach((box) => renderQuickBundle(box));
}, true);

// Andere Variante: Preise der Stufen neu. Anderes Produkt (Farbe als eigenes Produkt): Inhalt wird ersetzt, danach neu aufbauen
// (getAttribute: form.id wäre das Feld name="id" des Formulars)
document.addEventListener('variant:change', (event) => {
  document.querySelectorAll(`[data-occulto-quick-bundle][data-form-id="${event.target.getAttribute('id')}"]`)
    .forEach((box) => renderQuickBundle(box, event.detail?.variant?.id));
}, true);

// Microtask statt setTimeout: läuft, nachdem Prestige den Inhalt ersetzt hat, aber vor dem Zeichnen.
// Sonst ist beim Farbwechsel kurz der Normalpreis zu sehen.
document.addEventListener('product:rerender', () => {
  queueMicrotask(() => {
    // Neu gezeichneter Inhalt: Bundler neu aufbauen bzw. die gewählte Stufe (Preis wie im Sale) erneut setzen
    document.querySelectorAll('quick-buy-modal[open] [data-occulto-quick-bundle], [data-occulto-bundle-scope] [data-occulto-quick-bundle], .product-info [data-occulto-quick-bundle]').forEach((box) => {
      const radio = box.querySelector('input[type="radio"]:checked');
      if (box.hidden || !radio) renderQuickBundle(box);
      else selectQuickBundleTier(box, Number(radio.value));
    });
  });
}, true);

// Produkt-Highlight der Startseite (Handy): Pfeile blättern ein Bild weiter bzw. zurück
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-occulto-highlight-arrow]');
  const media = arrow?.parentElement.querySelector('[data-occulto-highlight-media]');
  if (media) media.scrollBy({ left: Number(arrow.dataset.occultoHighlightArrow) * media.clientWidth, behavior: 'smooth' });
});

// Produktseite und Produkt-Highlight der Startseite: Bundler gleich beim Laden aufbauen (im Quick-Buy erst beim Öffnen)
document.querySelectorAll('[data-occulto-bundle-scope] [data-occulto-quick-bundle], .product-info [data-occulto-quick-bundle]').forEach((box) => {
  if (!box.closest('quick-buy-modal')) renderQuickBundle(box);
});

// Produktseite: Klick auf die Sterne springt zu den Bewertungen (wie früher beim Judge.me-Stern-Block)
document.addEventListener('click', (event) => {
  if (!event.target.closest('.product-info .rating-badge')) return;
  document.getElementById('judgeme_product_reviews')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// Produktseite und Quick-Add: „nur … / Paar“ mit dem Preis der gewählten Mengenstaffel (Fly Bundles) neu berechnen,
// z. B. 2 Boxen à 3 Paar für 28,48 € → 28,48 € / 6 = 4,75 €. Normal auf volle Cent gerundet; ergibt das ,00 oder ,50,
// einen Cent weniger (5,00 → 4,99, 4,50 → 4,49) – dieselbe Regel wie in price-list.liquid
const parseEuro = (text) => parseFloat(text.replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
const formatEuro = (value) => `${value.toFixed(2).replace('.', ',')}€`;

const updatePricePerUnit = () => {
  document.querySelectorAll('.unit-price-extra[data-pack-size]').forEach((element) => {
    const scope = element.closest('quick-buy-modal') || element.closest('.shopify-section') || document;
    const tier = scope.querySelector('[data-offer-id][data-qty-label]:has(input[type="radio"]:checked)');
    const packSize = parseFloat(element.dataset.packSize);
    if (!tier || !packSize) return;

    const boxes = parseFloat(tier.dataset.qtyLabel) || 1;
    const total = parseEuro(tier.querySelector('.gfbVolumeDiscountDiscountPrice')?.textContent || '');
    if (!total) return;

    let cents = Math.round((Math.round(total * 100) / (boxes * packSize)) + 1e-9);
    if (cents > 0 && cents % 50 === 0) cents -= 1;
    const perUnit = cents / 100;
    const text = `nur ${formatEuro(perUnit)} / ${element.dataset.packUnit}`;
    if (element.textContent !== text) element.textContent = text;
  });
};

// Fly Bundles und Prestige zeichnen Staffel und Preis bei jeder Auswahl neu, deshalb auf Änderungen im Dokument reagieren
let pricePerUnitFrame;
new MutationObserver(() => {
  cancelAnimationFrame(pricePerUnitFrame);
  pricePerUnitFrame = requestAnimationFrame(updatePricePerUnit);
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'class'] });
document.addEventListener('change', updatePricePerUnit);

// Fly Bundles setzt die Auswahl beim Klick per Skript (ohne change-Event) und stoppt den Klick, deshalb in der
// Capture-Phase mithören und kurz danach neu rechnen
document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-offer-id][data-qty-label]')) return;
  setTimeout(updatePricePerUnit, 50);
  setTimeout(updatePricePerUnit, 400);
}, true);

// „Das könnte dir auch gefallen“ (snippets/occulto-cart-upsell.liquid): Variante wechseln und hinzufügen.
// Der Warenkorb wird danach neu gezeichnet und zeigt dann die nächste Empfehlung.
const showUpsellVariant = (upsell, variant) => {
  const price = upsell.querySelector('[data-upsell-price]');
  price.textContent = variant.price;
  price.classList.toggle('text-on-sale', variant.compare !== '');
  upsell.querySelector('[data-upsell-compare]').textContent = variant.compare;
  upsell.querySelectorAll('[data-upsell-link]').forEach((link) => (link.href = variant.url));

  const image = upsell.querySelector('.occulto-cart-upsell__image img');
  if (image && variant.image) {
    image.removeAttribute('srcset');
    image.src = variant.image;
  }
};

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-occulto-cart-upsell] select');
  if (!select) return;

  const upsell = select.closest('[data-occulto-cart-upsell]');
  const form = upsell.querySelector('[data-upsell-form]');
  const colorSelect = form.querySelector('[data-upsell-color]');

  // Ein Auswahlfeld mit allen Varianten
  if (!colorSelect) {
    const option = select.selectedOptions[0];
    showUpsellVariant(upsell, { ...option.dataset });
    return;
  }

  // Farbe und Größe getrennt: Größen ohne lieferbare Variante in dieser Farbe sperren, sonst nächste lieferbare Größe
  const variants = JSON.parse(form.querySelector('[data-upsell-variants]').textContent).filter(Boolean);
  const sizeSelect = form.querySelector('[data-upsell-size]');
  const sizesForColor = variants.filter((variant) => variant.color === colorSelect.value).map((variant) => variant.size);

  [...sizeSelect.options].forEach((option) => (option.disabled = !sizesForColor.includes(option.value)));
  if (!sizesForColor.includes(sizeSelect.value)) sizeSelect.value = sizesForColor[0];

  const variant = variants.find((item) => item.color === colorSelect.value && item.size === sizeSelect.value);
  if (!variant) return;

  form.elements.id.value = variant.id;
  showUpsellVariant(upsell, variant);
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-upsell-form]');
  if (!form) return;

  event.preventDefault();
  const button = form.querySelector('button');
  button.disabled = true;

  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }] })
  });

  if (!response.ok) {
    button.textContent = (await response.json()).description || 'Nicht verfügbar';
    return;
  }

  // Für die Auswertung: Varianten aus dieser Empfehlung als unsichtbares Warenkorb-Attribut (steht später in der Bestellung)
  const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
  const added = [cart.attributes?._warenkorb_upsell, form.elements.id.value].filter(Boolean).join(',');
  await fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _warenkorb_upsell: added } })
  });

  document.dispatchEvent(new CustomEvent('cart:refresh'));
});
/* ##Custom Code Ende## */

// Warenkorb: Fly hängt an die Angebots-ID (_flyBundles:OfferId) je Seitenaufruf eine neue Zufallsendung. Kommt dieselbe
// Variante nach einem Neuladen noch einmal dazu, unterscheiden sich die Eigenschaften und Shopify legt eine zweite Zeile
// an. Vor dem Hinzufügen deshalb die ID einer vorhandenen Zeile übernehmen, wenn alles andere gleich ist; dann führt
// Shopify die Zeilen zusammen.
const OFFER_KEY = '_flyBundles:OfferId';
const sameExceptOffer = (a = {}, b = {}) => {
  const strip = (props) => JSON.stringify(Object.entries(props).filter(([key]) => key !== OFFER_KEY).map(([key, value]) => [key, String(value)]).sort());
  return strip(a) === strip(b);
};
const matchingOffer = (lines, variantId, properties) => {
  const base = String(properties[OFFER_KEY]).split('_')[0];
  return lines.find((line) => String(line.variant_id) === String(variantId)
    && String(line.properties?.[OFFER_KEY] || '').split('_')[0] === base
    && sameExceptOffer(line.properties, properties))?.properties[OFFER_KEY];
};

const nativeFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input?.url || '';
  if (/\/cart\/add(\.js)?($|\?)/.test(url) && init?.body) {
    try {
      const body = init.body;
      const json = typeof body === 'string' && body.trim().startsWith('{') ? JSON.parse(body) : null;
      const hasOffer = body instanceof FormData ? body.has(`properties[${OFFER_KEY}]`) : JSON.stringify(json || {}).includes(OFFER_KEY);
      if (hasOffer) {
        const lines = (await nativeFetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json())).items;
        if (body instanceof FormData) {
          const properties = {};
          body.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          });
          const offer = matchingOffer(lines, body.get('id'), properties);
          if (offer) body.set(`properties[${OFFER_KEY}]`, offer);
        } else if (json) {
          (json.items || [json]).forEach((item) => {
            const offer = item.properties?.[OFFER_KEY] && matchingOffer(lines, item.id, item.properties);
            if (offer) item.properties[OFFER_KEY] = offer;
          });
          init = { ...init, body: JSON.stringify(json) };
        }
      }
    } catch (error) {
      // Im Zweifel unverändert hinzufügen (dann höchstens eine zweite Zeile)
    }
  }
  return nativeFetch(input, init);
};
